"use client"

import React, { useState, useEffect } from 'react'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  User,
  Building2,
  Mail,
  Phone,
  Cake,
  Home,
  Users,
  School,
  Loader2,
  Save,
  X
} from "lucide-react"
import { useUserProfile } from "@/hooks/stores/userStore"
import { AuthStore } from "@/hooks/stores/AuthStore"

interface EditPersonalInfoModalProps {
  isOpen: boolean
  onClose: () => void
}

interface PersonalInfoFormData {
  name: string
  grade: string
  email: string
  phoneNumber: string
  dateOfBirth: string
  address: string
  schoolName: string
  parentName: string
  parentEmail: string
  parentPhone: string
}

export function EditPersonalInfoModal({ isOpen, onClose }: EditPersonalInfoModalProps) {
  const userProfile = useUserProfile()
  const { updateProfile } = AuthStore()

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formData, setFormData] = useState<PersonalInfoFormData>({
    name: '',
    grade: 'Grade 10',
    email: '',
    phoneNumber: '',
    dateOfBirth: '',
    address: '',
    schoolName: '',
    parentName: '',
    parentEmail: '',
    parentPhone: '',
  })

  // Initialize form with user data when modal opens
  useEffect(() => {
    if (userProfile && isOpen) {
      // Format date for input field (YYYY-MM-DD)
      let formattedDate = ''
      if (userProfile.dateOfBirth) {
        try {
          const date = new Date(userProfile.dateOfBirth)
          if (!isNaN(date.getTime())) {
            formattedDate = date.toISOString().split('T')[0]
          }
        } catch (error) {
          console.error('Error formatting date:', error)
        }
      }

      setFormData({
        name: userProfile.name || '',
        grade: userProfile.grade || 'Grade 10',
        email: userProfile.email || '',
        phoneNumber: userProfile.phoneNumber || '',
        dateOfBirth: formattedDate,
        address: userProfile.address || '',
        schoolName: userProfile.schoolName || '',
        parentName: userProfile.parentName || '',
        parentEmail: userProfile.parentEmail || '',
        parentPhone: userProfile.parentPhone || '',
      })
      setError(null)
    }
  }, [userProfile, isOpen])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    if (error) setError(null)
  }

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    if (error) setError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    try {
      // Validate required fields
      if (!formData.name.trim()) {
        throw new Error('Full name is required')
      }
      if (!formData.email.trim()) {
        throw new Error('Email is required')
      }

      // Prepare updated data
      const updatedData = {
        name: formData.name.trim(),
        grade: formData.grade,
        email: formData.email.trim(),
        phoneNumber: formData.phoneNumber.trim() || undefined,
        dateOfBirth: formData.dateOfBirth ? new Date(formData.dateOfBirth) : undefined,
        address: formData.address.trim() || undefined,
        schoolName: formData.schoolName.trim() || undefined,
        parentName: formData.parentName.trim() || undefined,
        parentEmail: formData.parentEmail.trim() || undefined,
        parentPhone: formData.parentPhone.trim() || undefined,
      }

      // Update profile via AuthStore (which updates UserStore)
      await updateProfile(updatedData)

      // Close modal on success
      onClose()

    } catch (error) {
      console.error('Error updating profile:', error)
      setError(error instanceof Error ? error.message : 'Failed to update profile information')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      onClose()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            <User className="w-6 h-6 text-blue-600" />
            Edit Personal Information
          </DialogTitle>
          <DialogDescription>
            Update your personal information. All changes are saved automatically.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              <strong>Error:</strong> {error}
            </div>
          )}

          {/* Personal Information Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Personal Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-700">
                  Full Name *
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="pl-10"
                    placeholder="John Doe"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="grade" className="text-slate-700">
                  Grade/Class *
                </Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                  <select
                    id="grade"
                    name="grade"
                    value={formData.grade}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    disabled={isLoading}
                  >
                    <option value="Grade 7">Grade 7</option>
                    <option value="Grade 8">Grade 8</option>
                    <option value="Grade 9">Grade 9</option>
                    <option value="Grade 10">Grade 10</option>
                    <option value="Grade 11">Grade 11</option>
                    <option value="Grade 12">Grade 12</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">
                  Email *
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="pl-10"
                    placeholder="student@example.com"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phoneNumber" className="text-slate-700">
                  Phone Number
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                  <Input
                    id="phoneNumber"
                    name="phoneNumber"
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    className="pl-10"
                    placeholder="08123456789"
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="dateOfBirth" className="text-slate-700">
                  Date of Birth
                </Label>
                <div className="relative">
                  <Cake className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                  <Input
                    id="dateOfBirth"
                    name="dateOfBirth"
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    className="pl-10"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="schoolName" className="text-slate-700">
                  School Name
                </Label>
                <div className="relative">
                  <School className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                  <Input
                    id="schoolName"
                    name="schoolName"
                    value={formData.schoolName}
                    onChange={handleChange}
                    className="pl-10"
                    placeholder="Your School Name"
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address" className="text-slate-700">
                Address
              </Label>
              <div className="relative">
                <Home className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                <textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleTextareaChange}
                  className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[80px] resize-y"
                  placeholder="123 Main Street, Lagos, Nigeria"
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>

          {/* Parent/Guardian Information Section */}
          <div className="space-y-4 pt-4 border-t border-slate-200">
            <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Users className="w-5 h-5 text-teal-600" />
              Parent/Guardian Information
            </h3>

            <div className="space-y-2">
              <Label htmlFor="parentName" className="text-slate-700">
                Parent/Guardian Name
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                <Input
                  id="parentName"
                  name="parentName"
                  value={formData.parentName}
                  onChange={handleChange}
                  className="pl-10"
                  placeholder="Jane Doe"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="parentEmail" className="text-slate-700">
                  Parent Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                  <Input
                    id="parentEmail"
                    name="parentEmail"
                    type="email"
                    value={formData.parentEmail}
                    onChange={handleChange}
                    className="pl-10"
                    placeholder="parent@example.com"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="parentPhone" className="text-slate-700">
                  Parent Phone
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                  <Input
                    id="parentPhone"
                    name="parentPhone"
                    type="tel"
                    value={formData.parentPhone}
                    onChange={handleChange}
                    className="pl-10"
                    placeholder="08123456789"
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-3 sm:gap-0 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
              className="flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-2 shadow-sm hover:shadow-md transition-all"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}